import { onScopeDispose, readonly, ref } from 'vue';

/**
 * Ключ пересоздания активной страницы. Живёт на уровне модуля (общий синглтон):
 * `App.vue` вешает его на `<router-view :key>`, а любое место приложения может
 * вызвать `reloadCurrentPage()`. Смена ключа уничтожает и заново монтирует текущий
 * компонент страницы — срабатывает `onMounted`, данные подгружаются заново.
 *
 * Это «мягкая» перезагрузка: в отличие от `window.location.reload()`, приложение
 * (Apollo, Pinia, авторизация) не переинициализируется и белый экран не мелькает.
 */
const reloadKey = ref(0);

/** Разрешает ли перезагрузку; `false` — страница держит несохранённую работу. */
export type PageReloadGuard = () => boolean | Promise<boolean>;

/**
 * Guard'ы смонтированных потребителей. Набор, а не одна ссылка: страница и
 * открытая на ней форма защищаются одновременно, и вложенная не затирает
 * защиту основной.
 */
const guards = new Set<PageReloadGuard>();

/** Пока идёт опрос guard'ов, повторный запрос отклоняется: второго диалога быть не должно. */
let asking = false;

export function usePageReload() {
  /** Регистрирует проверку на время жизни текущего scope. */
  const registerReloadGuard = (guard: PageReloadGuard): void => {
    guards.add(guard);

    onScopeDispose(() => {
      guards.delete(guard);
    });
  };

  /**
   * Спрашивает guard'ы по очереди и пересоздаёт страницу, только если все
   * разрешили. Возвращает, состоялась ли перезагрузка.
   */
  const reloadCurrentPage = async (): Promise<boolean> => {
    if (asking) {
      return false;
    }

    asking = true;

    try {
      for (const guard of [...guards]) {
        if (!(await guard())) {
          return false;
        }
      }
    } finally {
      asking = false;
    }

    reloadKey.value += 1;

    return true;
  };

  return { registerReloadGuard, reloadCurrentPage, reloadKey: readonly(reloadKey) };
}
